<template>
  <div>
    <h2>{{ $t('SAAI.ViewResult.title') }}</h2>
    <div id="title-wrapper">
      <div>
        <p>
          <b>{{ $t('SAAI.ViewResult.explanation') }}</b>
        </p>
      </div>
      <div class="button-wrapper" id="sheet-button-wrapper">
        <button
          v-for="sheetNumber in this.numberOfSheets"
          :key="sheetNumber"
          @click="setSheetNumber(sheetNumber - 1)"
        >
          {{ paperOriginData.data[sheetNumber - 1].sheet }}
        </button>
      </div>
    </div>
    <div>
      <div id="checkboxContainer">
        <b-form-checkbox-group
          v-model="selectedImportantWords[sheetNumber]"
          :options="importantWordsList[this.sheetNumber]"
          value-field="keyword"
          text-field="keyword"
        ></b-form-checkbox-group>
      </div>
    </div>
    <div class="final-analysis-result-table-wrapper">
      <TabulatorComponent
        class="final-analysis-result-table"
        v-model="finalAnalysisResultData"
        :options="finalAnalysisResultOptions"
        ref="finalAnalysisResultTable"
      />
    </div>
    <div class="button-wrapper" id="final-result-button-wrapper">
      <button @click="downloadData()">{{ $t('SAAI.ViewResult.download') }}</button>
      <button @click="completeGrading()">{{ $t('SAAI.ViewResult.finish') }}</button>
    </div>
  </div>
</template>

<script>
import { TabulatorComponent } from 'vue-tabulator';
import { mapState, mapGetters, mapActions } from 'vuex';
import { postGradingResultData } from '@/api/gradingPaper';
import { fileDownloadSaai } from '@/modules/fileController';

export default {
  name: 'ViewResult',
  components: {
    TabulatorComponent,
  },
  data() {
    return {
      sheetNumber: 0,
      finalAnalysisResultData: [],
      finalAnalysisResultOptions: {
        height: '50vh',
        placeholder:
          this.$i18n.locale === 'eng' ? 'Loading results..' : '결과를 불러오는 중입니다..',
        columns: [
          { title: 'id', field: 'id', width: `7%` },
          {
            title: this.$i18n.locale === 'eng' ? 'response' : '응답',
            field: 'answer',
            width: `27%`,
          },
          {
            title: this.$i18n.locale === 'eng' ? 'interval' : '구간',
            field: 'interval',
            width: `8%`,
          },
          {
            title: this.$i18n.locale === 'eng' ? 'num' : '단어수',
            field: 'number_of_import_words',
            width: `8%`,
          },
          {
            title: this.$i18n.locale === 'eng' ? 'score' : '점수',
            field: 'score',
            formatter: function (cell) {
              return cell.getValue().toFixed(4);
            },
            width: `8%`,
          },
          {
            title: this.$i18n.locale === 'eng' ? 'missing important keywords' : '빠진 중요어',
            field: 'missing_important_word',
            width: `30%`,
          },
          {
            title: this.$i18n.locale === 'eng' ? 'feedback' : '피드백 문구',
            field: 'feedbackComment',
            width: `12%`,
          },
        ],
      },
      selectedImportantWords: [],
    };
  },
  computed: {
    ...mapState('saaiPaper', {
      finalAnalysisResults: (state) => state.finalAnalysisResults,
      numberOfSheets: (state) => state.numberOfSheets,
      paperOriginData: (state) => state.paperOriginData,
      importantWordsList: (state) => state.importantWordsList,
    }),
    ...mapGetters('saaiPaper', ['GE_GRADING_RESULT_DATA']),
  },
  methods: {
    ...mapActions('saaiPaper', ['AC_INIT_NOW_SHEET_NUMBER']),
    setSheetNumber(sheetNumber) {
      this.sheetNumber = sheetNumber;
    },
    completeGrading() {
      postGradingResultData(this.GE_GRADING_RESULT_DATA);
      this.$router.push('/saai');
    },
    setImportantWordsList() {
      const selectedWordsList = this.selectedImportantWords;
      const importantWordsList = this.importantWordsList;

      for (let i = 0; i < this.numberOfSheets; i++) {
        selectedWordsList[i] = [];
        for (const words of importantWordsList[i]) {
          selectedWordsList[i].push(words.keyword);
        }
      }
    },
    downloadData() {
      const deepCopy = require('lodash');
      let excelResults = deepCopy.cloneDeep(this.finalAnalysisResults.slice());
      
      const questions = [];
      this.paperOriginData.data.forEach((sheet) => {
        let question = sheet.data[1][1]
        questions.push(question);
      })

      for (const page in excelResults) {
        const pageImportantWord = this.selectedImportantWords[page];
        for (const answer of excelResults[page]) {
          const missing_word = answer.missing_important_word;
          if (pageImportantWord.length > 0) {
            missing_word = missing_word.filter((element) => pageImportantWord.includes(element));
          } else {
            missing_word = [];
          }
          answer.missing_important_word = missing_word;
        }
      }
      fileDownloadSaai(excelResults, questions, true, this.$i18n.locale);
    },
  },
  watch: {
    sheetNumber() {
      this.finalAnalysisResultData = this.finalAnalysisResults[this.sheetNumber];
    },
    '$i18n.locale': function (newLocale, oldLocale) {
      this.finalAnalysisResultOptions.columns[1].title = newLocale === 'eng' ? 'response' : '응답';
      this.finalAnalysisResultOptions.columns[2].title = newLocale === 'eng' ? 'interval' : '구간';
      this.finalAnalysisResultOptions.columns[3].title = newLocale === 'eng' ? 'num' : '단어수';
      this.finalAnalysisResultOptions.columns[4].title = newLocale === 'eng' ? 'score' : '점수';
      this.finalAnalysisResultOptions.columns[5].title =
        newLocale === 'eng' ? 'missing important keywords' : '빠진 중요어';
      this.finalAnalysisResultOptions.columns[6].title =
        newLocale === 'eng' ? 'feedback' : '피드백 문구';
    },
  },
  created() {
    const currentRoutePath = JSON.parse(localStorage.getItem('currentRoutePath'));
    if (currentRoutePath === '/saai/viewResult' && currentRoutePath !== '/saai/setFeedback') {
      localStorage.removeItem('currentRoutePath');
      this.$router.push('/saai/create');
    }

    for (let result = 0; result < this.finalAnalysisResults.length; result++) {
      this.finalAnalysisResults[result] = this.finalAnalysisResults[result].sort(
        (a, b) => a.id - b.id
      );
    }
    this.finalAnalysisResultData = this.finalAnalysisResults[this.sheetNumber];
    this.AC_INIT_NOW_SHEET_NUMBER();
    this.setImportantWordsList();
  },
  beforeRouteLeave(to, from, next) {
    let confirmText =
      this.$i18n.locale === 'eng'
        ? 'You may lose data. Do you want to continue?'
        : '데이터가 손실될 수 있습니다. 계속 진행하시겠습니까?';
    if (to.path === '/saai/setFeedback' && !confirm(confirmText)) {
      next(false);
    } else {
      next();
    }
  },
};
</script>

<style lang="scss">
#title-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 7px;
  margin-bottom: -3px;
}

.title-container {
  display: block;
  margin-bottom: 15px;
}

.final-analysis-result-table-wrapper {
  margin: 10px 0;
}

#sheet-button-wrapper {
  margin: 5px 0 0 0;
}

#final-result-button-wrapper,
#sheet-button-wrapper {
  justify-content: right;
}

#checkboxContainer {
  padding: 10px;
  margin: 15px 0;
  border: 1px solid $MAIN-COLOR;
  border-radius: 7px;
}
</style>
